/**
 * wtshuang@cs.uw.edu
 * 2018/01/17
 * The panel for the question. Integrated in instance panel.
 */

import * as React from 'react';
import { observer } from 'mobx-react';
import { Popup, Button } from 'semantic-ui-react';
import * as d3 from 'd3';

import { Token, FlagDatum } from '../stores/Interfaces';
import { Question } from '../stores/Question';
import { Answer } from '../stores/Answer';

import { utils } from '../stores/Utils';

import '../css/App.css';

export interface QuestionProps {
    question: Question;
    attention: number[][];
    toggleTokens: boolean[];
    selectTokenHandler: (token: Token) => void;
    mouseoveredHandler: (idx: number) => void;
    mouseoutHandler: () => void;
    hoveredIdx: number;
    selectedToken: Token|null;
    answers: Answer[];
    selectBlockHandler: (flags: FlagDatum[]) => void;
    toggleTokenHandler: (idx: number) => void;
}

@observer
export class QuestionPanel extends React.Component<QuestionProps, {}> {
    // view related
    private viewType: string;
    // imported from the upper view
    private qid: string
    private question: Question;
    private colorScale: {
        entity: d3.ScaleQuantile<string>;
        others: d3.ScaleQuantile<string>;
     } 

    private selectHandler: (token: Token) => void = this.props.selectTokenHandler;

    constructor (props: QuestionProps, context: any) {
        super(props, context);
        this.viewType = 'qpanel';
        this.question = this.props.question;
        this.qid = this.question.qid;
        this.renderToken = this.renderToken.bind(this);
        this.colorScale = {
            entity: d3.scaleQuantile<string>().domain([0, 1.01]).range(utils.attentionColor.entity),
            others: d3.scaleQuantile<string>().domain([0, 1.01]).range(utils.attentionColor.others)
        }
        // initiate the input
    }

    /**
     * get the color for a given t, based on the currently selected answer
     * @param t <Token> the targeting token.
     * @return <string> a color.
     */
    private answerBackgroundColorFunc (t: Token): string {
        let color = 'rgba(255, 255, 255, 0)';
        if (this.props.hoveredIdx === -1) {
            return color;
        } else {
            color = this.colorScale.others(this.props.attention[this.props.hoveredIdx][t.idx]);
        }
        return color;
    }

    public renderToken(t: Token, idx: number): JSX.Element {
        // generate the current class for the token
        // get the current span
        const curClass = utils.genClass(this.viewType, 'qtoken', [this.qid, utils.genStrId(t.text), t.idx]);
        const selected = this.props.selectedToken !== null && t.idx === this.props.selectedToken.idx ? 'selected' : '';
        const annotation = t.ner === '' ? '' : idx > 0 
            && this.question.doc[idx-1].ner !== t.ner ? t.ner : '';
        const curSpan: JSX.Element = <span 
            key={ curClass.key }
            className={ `token ${curClass.total} ${selected}` }
            id={ curClass.key }
            onClick={() => {this.selectHandler(t); }}
            onMouseOut={() => {this.props.selectBlockHandler([])}}
            onMouseOver={() => {t.ner === '' ? null : this.props.selectBlockHandler([{ftype: 'ner', value: t.ner}] )}}
            ><div style={{paddingTop: 1, verticalAlign: 'bottom'}} className='token-info-block'>{annotation.toUpperCase()}</div>
            <div style={{
                paddingTop: 0, marginTop: -5, paddingBottom: 0, marginBottom: 0,
                backgroundColor: this.answerBackgroundColorFunc(t)
            }}>
            <b>{ t.text + t.whitespace }</b></div></span>;
        const popup: JSX.Element = <span
            key = { `${curClass.key}-popspan` }>
            <b>NER: </b>{t.ner} | 
            <b>POS: </b>{t.pos} | 
            <b>LEMMA: </b>{t.lemma}
            <div><Button size='mini' 
                icon='toggle off' content='Toggle attention' 
                onClick={() => {this.props.toggleTokenHandler(idx)}}
                active={this.props.toggleTokens[idx]} inverted basic/></div></span>;
        return <Popup size='mini' inverted hoverable 
            position='top center' 
            key={ `${curClass.key}-popup` }
            trigger={curSpan} content={popup} />;
    }
    
    /**
     * Render the question bar.
     */
    public render(): JSX.Element {
        // each row should be one question token.  N_p * N_q -> N_q * N_p
        return <div className={'full-width'}>{this.question.doc.map((t: Token, idx: number) => this.renderToken(t, idx))}</div>;
    }
}


// WEBPACK FOOTER //
// ./src/components/QuestionPanel.tsx